import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Typography,
} from "antd";
import {
  getChargeTypeOptions,
  getParticularOptions,
} from "apiServices/transportationCostService";
import { useEffect, useMemo, useState } from "react";

const { Text } = Typography;

const formatAmount = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toNumberOrUndefined = (value) =>
  value === null || value === undefined || value === ""
    ? undefined
    : Number(value);

export const transportChargeFormValues = (record) => ({
  transport_vehicles: (record?.transport_vehicles || []).map((vehicle) => ({
    transport_particular_id: vehicle.transport_particular_id,
    vehicle_number: vehicle.vehicle_number,
    charge_amount: toNumberOrUndefined(vehicle.charge_amount),
  })),
  transport_charges: (record?.transport_charges || []).map((charge) => ({
    transport_charge_type_id: charge.transport_charge_type_id,
    amount: toNumberOrUndefined(charge.amount),
  })),
});

export const transportChargePayload = (values) => ({
  transport_vehicles: (values?.transport_vehicles || []).map((vehicle) => ({
    transport_particular_id: vehicle.transport_particular_id,
    vehicle_number: vehicle.vehicle_number || null,
    charge_amount: vehicle.charge_amount ?? null,
  })),
  transport_charges: (values?.transport_charges || []).map((charge) => ({
    transport_charge_type_id: charge.transport_charge_type_id,
    amount: charge.amount,
  })),
});

/**
 * Must be rendered inside the parent antd <Form>; values live under
 * `transport_vehicles` and `transport_charges`.
 */
const ShipmentTransportCharges = ({ isExport }) => {
  const form = Form.useFormInstance();
  const particularType = isExport ? "export" : "import";
  const typeLabel = isExport ? "Export" : "Import";

  const [particulars, setParticulars] = useState([]);
  const [chargeTypes, setChargeTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const vehicles = Form.useWatch("transport_vehicles", form);
  const charges = Form.useWatch("transport_charges", form);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      const [particularRes, chargeTypeRes] = await Promise.all([
        getParticularOptions(particularType),
        getChargeTypeOptions(),
      ]);
      if (particularRes?.status === 200)
        setParticulars(particularRes.data?.data || []);
      if (chargeTypeRes?.status === 200)
        setChargeTypes(chargeTypeRes.data?.data || []);
      setLoading(false);
    };
    loadOptions();
  }, [particularType]);

  const vehicleChargeTotal = useMemo(
    () =>
      (vehicles || []).reduce(
        (sum, row) => sum + (Number(row?.charge_amount) || 0),
        0,
      ),
    [vehicles],
  );
  const otherChargeTotal = useMemo(
    () =>
      (charges || []).reduce((sum, row) => sum + (Number(row?.amount) || 0), 0),
    [charges],
  );

  const isMarketPrice = (particularId) => {
    const particular = particulars.find((item) => item.id === particularId);
    return !!particular && particular.rate === null;
  };

  const onParticularChange = (fieldName, particularId) => {
    const particular = particulars.find((item) => item.id === particularId);
    form.setFieldValue(
      ["transport_vehicles", fieldName, "charge_amount"],
      particular && particular.rate !== null
        ? Number(particular.rate)
        : undefined,
    );
  };

  const onChargeTypeSelect = (fieldName, chargeTypeId) => {
    const chargeType = chargeTypes.find((item) => item.id === chargeTypeId);
    if (chargeType && chargeType.amount !== null) {
      form.setFieldValue(
        ["transport_charges", fieldName, "amount"],
        Number(chargeType.amount),
      );
    }
  };

  return (
    <>
      <Card
        title={`Vehicles — Select Particular (As per Agreement with Raj Paribahan for ${typeLabel})`}
        style={{ marginTop: 16 }}
      >
        <Form.List name="transport_vehicles">
          {(fields, { add, remove }) => (
            <>
              <Table
                size="small"
                bordered
                pagination={false}
                rowKey={(field) => field.key}
                dataSource={fields}
                locale={{ emptyText: "No vehicles added" }}
                columns={[
                  {
                    title: "SL",
                    width: 50,
                    align: "center",
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: "Particular",
                    width: "50%",
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, "transport_particular_id"]}
                        rules={[
                          { required: true, message: "Select particular" },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          size="small"
                          placeholder="Select particular"
                          loading={loading}
                          optionFilterProp="label"
                          notFoundContent={`No ${particularType} particular configured`}
                          onChange={(id) => onParticularChange(field.name, id)}
                          options={particulars.map((item) => ({
                            value: item.id,
                            label: `${item.particular} — ${
                              item.rate !== null
                                ? formatAmount(item.rate)
                                : "Market price"
                            }`,
                          }))}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Vehicle Number",
                    width: "20%",
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, "vehicle_number"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          size="small"
                          placeholder="Vehicle number"
                          maxLength={50}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Charge Amount",
                    width: "20%",
                    render: (_, field) => {
                      const marketPrice = isMarketPrice(
                        vehicles?.[field.name]?.transport_particular_id,
                      );
                      return (
                        <Form.Item
                          name={[field.name, "charge_amount"]}
                          rules={[
                            {
                              required: marketPrice,
                              message: "Enter market price amount",
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            size="small"
                            style={{ width: "100%" }}
                            min={0}
                            precision={2}
                            placeholder={
                              marketPrice ? "Enter amount" : undefined
                            }
                            disabled={!marketPrice}
                          />
                        </Form.Item>
                      );
                    },
                  },
                  {
                    title: "Add",
                    width: 60,
                    align: "center",
                    render: (_, field) => (
                      <Button
                        size="small"
                        icon={<PlusCircleOutlined />}
                        onClick={() => add({}, field.name + 1)}
                        style={{ color: "green" }}
                        aria-label="Add vehicle"
                      />
                    ),
                  },
                  {
                    title: "Delete",
                    width: 60,
                    align: "center",
                    render: (_, field) => (
                      <Button
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(field.name)}
                        style={{ color: "red" }}
                        aria-label="Remove vehicle"
                      />
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4} align="right">
                        <Text strong>Vehicle Charge Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong>{formatAmount(vehicleChargeTotal)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} colSpan={2} />
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
              {!fields.length && (
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                  block
                  style={{ marginTop: 8 }}
                >
                  Add Vehicle
                </Button>
              )}
            </>
          )}
        </Form.List>
      </Card>

      <Card title="Other Charges" style={{ marginTop: 16, marginBottom: 30 }}>
        <Form.List name="transport_charges">
          {(fields, { add, remove }) => (
            <>
              <Table
                size="small"
                bordered
                pagination={false}
                rowKey={(field) => field.key}
                dataSource={fields}
                locale={{ emptyText: "No other charges" }}
                columns={[
                  {
                    title: "Charge Type",
                    width: "60%",
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, "transport_charge_type_id"]}
                        rules={[
                          { required: true, message: "Select charge type" },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          size="small"
                          placeholder="Select type"
                          loading={loading}
                          optionFilterProp="label"
                          options={chargeTypes.map((item) => ({
                            value: item.id,
                            label: item.name,
                          }))}
                          onSelect={(chargeTypeId) =>
                            onChargeTypeSelect(field.name, chargeTypeId)
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Amount",
                    width: "30%",
                    render: (_, field) => (
                      <Form.Item
                        name={[field.name, "amount"]}
                        rules={[{ required: true, message: "Enter amount" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          size="small"
                          min={0}
                          precision={2}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: "Delete",
                    width: 60,
                    align: "center",
                    render: (_, field) => (
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        aria-label="Remove row"
                      />
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} align="right">
                        <Text strong>Other Charges Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong>{formatAmount(otherChargeTotal)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} align="right">
                        <Text strong>
                          Grand Total (Vehicle Charges + Other Charges)
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong>
                          {formatAmount(vehicleChargeTotal + otherChargeTotal)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => add()}
                block
                style={{ marginTop: 8 }}
              >
                Add More
              </Button>
            </>
          )}
        </Form.List>
      </Card>
    </>
  );
};

export default ShipmentTransportCharges;
